import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import { CloseIcon, GlobeIcon } from 'assets/icons'
import { EventLocationRow } from 'components/eventComponents/EventLocation'
import NotesList from 'components/lists/NotesList'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { IEventDetails } from 'hooks/useEventDetails'
import i18n from 'i18n'
import { getDefaultTimezone } from 'index'
import { ReactNode } from 'react'
import { EventDoc, EventNote } from 'types/api/changes'
import { Model } from 'types/model'
import { isMobileDevice } from 'utils/constants'
import { provideEventTitle } from 'utils/event'
import EventCoverImage, { getCoverImageNote } from './EventCoverImage'
import StartEndDateRows from './StartEndDateRows'
import dayjs from 'dayjs'
import dateToTimeString from 'components/calendar/components'
import { MiscText } from 'components/login/styled'
import { t } from 'i18next'
import EventRecurrenceComponent from 'components/eventComponents/EventRecurrence'
import { notesWithoutCoverImage } from 'utils/note_meta_data'

interface SmallEventDialogProps {
  event: Model<EventDoc> | EventDoc
  eventHook: IEventDetails
  isUiBlocked: boolean
  timeZoneOpen: boolean
  setTimeZoneOpen: (b: boolean) => void
  removeTimeZone: () => void
  nonDefaultTimeZone: boolean
  onClose: () => void
}

export default function SmallEventDialog({
  event,
  eventHook,
  isUiBlocked,
  timeZoneOpen,
  setTimeZoneOpen,
  removeTimeZone,
  nonDefaultTimeZone,
  onClose
}: SmallEventDialogProps) {
  const theme = useTheme()
  const coverImageNote = getCoverImageNote(event.Notes)
  const hasCoverImage = !!coverImageNote

  function buildTimeZoneRow(value: string): ReactNode {
    let zonedStart = dayjs(Number(eventHook.getValues('StartDate')) * 1000).tz(
      getDefaultTimezone()
    )
    let zonedEnd = dayjs(Number(eventHook.getValues('EndDate')) * 1000).tz(
      getDefaultTimezone()
    )

    if (eventHook.getValues('AllDay') === '1') {
      zonedStart = dayjs(Number(eventHook.getValues('StartDate')) * 1000)
      zonedEnd = dayjs(Number(eventHook.getValues('EndDate')) * 1000)
    }

    let yourTimeZoneTime = ''
    const zonedStartDay = zonedStart.format('MMMM DD')
    const zonedEndDay = zonedEnd.format('MMMM DD')

    if (eventHook.timeZone !== getDefaultTimezone()) {
      yourTimeZoneTime = `(${t('yourTimeZone', {
        time: `${zonedStartDay}, ${dateToTimeString(
          zonedStart.toDate(),
          true
        )} - ${
          zonedEndDay !== zonedStartDay ? `${zonedEndDay}, ` : ''
        } ${dateToTimeString(zonedEnd.toDate(), true)}`
      })})`
    }

    return (
      <Grid container direction={'column'} gap={1}>
        {value}
        <MiscText
          fontFamily={'NotoSans'}
          fontWeight={'400 !important'}
          marginLeft={i18n.dir() === 'ltr' ? '6px' : undefined}
          marginRight={i18n.dir() === 'ltr' ? undefined : '6px'}
          color={theme.palette.text.secondary}
        >
          {yourTimeZoneTime}
        </MiscText>
      </Grid>
    )
  }

  const title = provideEventTitle(event)

  return (
    <Dialog
      open={true}
      onClose={onClose}
      scroll="paper"
      disableScrollLock={isMobileDevice()}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          width: isMobileDevice() ? 'auto' : '470px',
          maxWidth: isMobileDevice() ? '400px' : undefined,
          margin: isMobileDevice() ? '16px' : undefined,
          maxHeight: isMobileDevice()
            ? 'calc(100vh - 32px)'
            : 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      sx={{ zIndex: 999997 }}
    >
      {/* Cover image section - edge to edge */}
      {hasCoverImage && (
        <Box
          sx={{
            position: 'relative',
            minHeight: '180px',
            overflow: 'hidden'
          }}
        >
          <EventCoverImage notes={event.Notes} />
          {/* Close button on image */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              [i18n.dir() === 'ltr' ? 'right' : 'left']: 8,
              zIndex: 2,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.2)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.35)' }
            }}
            size="small"
          >
            <CloseIcon style={{ color: 'white', width: 18, height: 18 }} />
          </IconButton>
          {/* Title overlay at bottom of image */}
          <Typography
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              padding: '40px 20px 14px 20px',
              zIndex: 1,
              color: 'white',
              fontSize: '1.4rem',
              fontWeight: 700,
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 100%)'
            }}
          >
            {title}
          </Typography>
        </Box>
      )}

      {/* Title + close for no-image variant */}
      {!hasCoverImage && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '16px 20px 0 20px'
          }}
        >
          <Typography
            sx={{
              fontSize: '1.4rem',
              fontWeight: 700,
              paddingTop: '4px',
              flex: 1
            }}
          >
            {title}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ marginTop: '-2px' }}>
            <CloseIcon style={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>
      )}

      {/* Content */}
      <DialogContent
        sx={{
          padding: '8px 20px 16px 20px',
          pointerEvents: 'auto',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <Box height={'20px'} />
        <StartEndDateRows
          initiallyExpanded={isMobileDevice()}
          eventHook={eventHook}
          event={event}
          timeZoneOpen={timeZoneOpen}
          setTimeZoneOpen={setTimeZoneOpen}
          removeTimeZone={removeTimeZone}
          nonDefaultTimeZone={nonDefaultTimeZone}
          readonly
        />

        {isMobileDevice() &&
          getDefaultTimezone() !== eventHook.timeZone &&
          eventHook.getValues('AllDay') !== '1' && (
            <PopupSectionRow
              icon={<GlobeIcon style={{ opacity: 1 }} />}
              onlyTitle
              title={
                <Box
                  marginLeft={i18n.dir() === 'ltr' ? '6px' : undefined}
                  marginRight={i18n.dir() === 'ltr' ? undefined : '6px'}
                >
                  {eventHook.timeZone}
                </Box>
              }
            />
          )}
        {getDefaultTimezone() !== eventHook.timeZone &&
          eventHook.getValues('AllDay') !== '1' && (
            <PopupSectionRow
              icon={<GlobeIcon style={{ opacity: 0 }} />}
              onlyTitle
              title={buildTimeZoneRow('')}
            />
          )}

        {event.Recurrence && (
          <EventRecurrenceComponent
            eventHook={eventHook}
            recurrence={eventHook.getValues('Recurrence')}
            setEventRecurrence={eventHook.setRecurrence}
            readonly
          />
        )}

        {event.Location && (event.Location?.Address?.length ?? 0) > 0 && (
          <EventLocationRow
            isUiBlocked={isUiBlocked}
            disallowEdit
            setLocation={eventHook.setLocation}
            eventLocation={event.Location}
          />
        )}
        <Box height={'20px'} />
        {notesWithoutCoverImage(event.Notes).length > 0 && (
          <PopupSection simplified title={`${t('notes')}`}>
            <Box height={4}></Box>
            <NotesList
              isUiBlocked={isUiBlocked}
              disableDelete
              checkable={false}
              notes={notesWithoutCoverImage(event.Notes)}
              event={event}
            />
          </PopupSection>
        )}
      </DialogContent>
    </Dialog>
  )
}
